import {
  formatCustomFieldValue,
  type CustomFieldDefinition,
  type CustomFieldRecordAvailability,
  type CustomFieldGroupSummary,
  type CustomFieldValue,
  type CustomFieldValues,
} from '@erp/shared';
import {
  BadgeCheck,
  Boxes,
  ChevronDown,
  CircleHelp,
  FileText,
  Info,
  Layers3,
  Scale,
  Shield,
  ShoppingCart,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  useCustomFieldGroupStates,
  useUpdateCustomFieldGroupState,
} from '../api/use-custom-fields';
import { customFieldLabel } from '../lib/custom-field-label';
import { CustomFieldControl } from './custom-field-control';

const GROUP_ICONS: Readonly<Record<string, LucideIcon>> = {
  info: Info,
  legal: Scale,
  scale: Scale,
  logistics: Truck,
  truck: Truck,
  security: Shield,
  shield: Shield,
  purchasing: ShoppingCart,
  'shopping-cart': ShoppingCart,
  quality: BadgeCheck,
  'badge-check': BadgeCheck,
  certificates: FileText,
  'file-text': FileText,
  boxes: Boxes,
  help: CircleHelp,
};

const EMPTY_AVAILABILITY: readonly CustomFieldRecordAvailability[] = [];
const EMPTY_ERRORS: Readonly<Record<string, string | undefined>> = {};
const EMPTY_COMPUTED: Readonly<Record<string, string | undefined>> = {};

interface FieldGroup {
  group: CustomFieldGroupSummary;
  definitions: CustomFieldDefinition[];
}

function isEmpty(value: CustomFieldValue | undefined): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0)
  );
}

function groupedDefinitions(
  definitions: readonly CustomFieldDefinition[],
  availability: readonly CustomFieldRecordAvailability[],
): FieldGroup[] {
  const grouped = new Map<string, FieldGroup>();
  for (const definition of definitions) {
    const bound = availability.some(
      (entry) => entry.definitionId === definition.id && entry.key === definition.key,
    );
    if (
      (!definition.isActive && !bound) ||
      definition.archivedAt !== null ||
      definition.status === 'ARCHIVED' ||
      definition.fieldGroup === null ||
      !definition.fieldGroup.isActive
    )
      continue;
    const current = grouped.get(definition.fieldGroup.id);
    if (current === undefined) {
      grouped.set(definition.fieldGroup.id, {
        group: definition.fieldGroup,
        definitions: [definition],
      });
    } else {
      current.definitions.push(definition);
    }
  }
  return [...grouped.values()]
    .map((entry) => ({
      ...entry,
      definitions: [...entry.definitions].sort(
        (left, right) => left.sortOrder - right.sortOrder || left.label.localeCompare(right.label),
      ),
    }))
    .sort(
      (left, right) =>
        left.group.sortOrder - right.group.sortOrder ||
        left.group.name.localeCompare(right.group.name),
    );
}

export interface CategorizedCustomFieldsProps {
  definitions: readonly CustomFieldDefinition[];
  recordAvailability?: readonly CustomFieldRecordAvailability[] | undefined;
  values: CustomFieldValues;
  mode: 'edit' | 'detail';
  onChange?: ((key: string, value: CustomFieldValue) => void) | undefined;
  errors?: Readonly<Record<string, string | undefined>> | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
  className?: string | undefined;
  /** Overrides the category setting in detail mode. */
  showEmpty?: boolean | undefined;
  computedValues?: Readonly<Record<string, string | undefined>> | undefined;
}

export function CategorizedCustomFields({
  definitions,
  recordAvailability = EMPTY_AVAILABILITY,
  values,
  mode,
  onChange,
  errors = EMPTY_ERRORS,
  disabled = false,
  idPrefix = 'custom-field',
  className,
  showEmpty,
  computedValues = EMPTY_COMPUTED,
}: CategorizedCustomFieldsProps): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const groupStates = useCustomFieldGroupStates();
  const { mutate: persistGroupState } = useUpdateCustomFieldGroupState();
  const [localStates, setLocalStates] = useState<Readonly<Record<string, boolean>>>({});
  const groups = useMemo(
    () => groupedDefinitions(definitions, recordAvailability),
    [definitions, recordAvailability],
  );
  const persistedStates = useMemo(
    () =>
      new Map((groupStates.data ?? []).map((state) => [state.groupId, state.isExpanded] as const)),
    [groupStates.data],
  );

  const expandedOf = (group: CustomFieldGroupSummary): boolean =>
    localStates[group.id] ?? persistedStates.get(group.id) ?? group.defaultExpanded;

  const setExpanded = (groupId: string, isExpanded: boolean): void => {
    setLocalStates((current) => ({ ...current, [groupId]: isExpanded }));
    persistGroupState({ groupId, isExpanded });
  };

  useEffect(() => {
    const invalidDefinition = definitions.find(
      (definition) => errors[definition.key] !== undefined,
    );
    const groupId = invalidDefinition?.fieldGroup?.id;
    if (invalidDefinition === undefined || groupId === undefined) return;
    setLocalStates((current) => ({ ...current, [groupId]: true }));
    if (persistedStates.get(groupId) === false) {
      persistGroupState({ groupId, isExpanded: true });
    }
    window.requestAnimationFrame(() => {
      const element = document.getElementById(`${idPrefix}-${invalidDefinition.key}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (element instanceof HTMLElement) element.focus({ preventScroll: true });
    });
  }, [definitions, errors, idPrefix, persistedStates, persistGroupState]);

  const visibleGroups = groups.flatMap((entry) => {
    if (mode === 'edit') return [entry];
    const fields = entry.definitions.filter(
      (definition) =>
        showEmpty === true ||
        (showEmpty === undefined && !entry.group.hideEmptyInDetail) ||
        !isEmpty(values[definition.key]),
    );
    return fields.length === 0 ? [] : [{ ...entry, definitions: fields }];
  });

  if (visibleGroups.length === 0) return null;

  return (
    <section
      id={`${idPrefix}-section`}
      data-custom-fields-section
      tabIndex={-1}
      className={cn('grid scroll-mt-24 gap-4 lg:grid-cols-2', className)}
    >
      {visibleGroups.map(({ group, definitions: groupDefinitions }) => {
        const expanded = expandedOf(group);
        const hasUnpublishedFields = groupDefinitions.some((definition) =>
          recordAvailability.some(
            (entry) =>
              entry.definitionId === definition.id &&
              entry.key === definition.key &&
              entry.unpublished,
          ),
        );
        const Icon = GROUP_ICONS[group.icon?.toLowerCase() ?? ''] ?? Layers3;
        return (
          <Card
            key={group.id}
            data-custom-field-group={group.id}
            className={cn(group.layoutWidth === 'FULL' && 'lg:col-span-2')}
            style={
              group.color === null
                ? undefined
                : { borderInlineStartColor: group.color, borderInlineStartWidth: 4 }
            }
          >
            <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-3">
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-start gap-3 whitespace-normal p-0 text-start hover:bg-transparent"
                aria-expanded={expanded}
                aria-controls={`${idPrefix}-group-${group.id}`}
                aria-label={expanded ? t('common.hideDetails') : t('common.showDetails')}
                onClick={() => setExpanded(group.id, !expanded)}
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <CardTitle>{group.name}</CardTitle>
                  {group.description === null ? null : (
                    <CardDescription className="mt-1">{group.description}</CardDescription>
                  )}
                </div>
                <ChevronDown
                  className={cn('h-4 w-4 shrink-0 transition-transform', expanded && 'rotate-180')}
                  aria-hidden="true"
                />
              </Button>
            </CardHeader>
            {expanded ? (
              <CardContent id={`${idPrefix}-group-${group.id}`} className="p-4 pt-1 sm:p-5 sm:pt-1">
                <div
                  className={cn(
                    'grid gap-4',
                    group.fieldLayout === 'TWO_COLUMNS' && 'sm:grid-cols-2',
                  )}
                >
                  {groupDefinitions.map((definition) => {
                    const available = recordAvailability.find(
                      (entry) =>
                        entry.definitionId === definition.id && entry.key === definition.key,
                    );
                    const unresolvedSourceValue = available?.unresolvedSourceValue ?? null;
                    const current = values[definition.key];
                    const unresolved =
                      definition.fieldType === 'SELECT' &&
                      typeof current === 'string' &&
                      current !== '' &&
                      unresolvedSourceValue !== null &&
                      !definition.options.some((option) => option.value === current);
                    const fullWidth =
                      definition.fieldType === 'WEEKLY_SCHEDULE' ||
                      definition.fieldType === 'TEXTAREA' ||
                      definition.fieldType === 'MULTI_SELECT' ||
                      definition.fieldType === 'FORMULA';
                    if (mode === 'edit') {
                      return (
                        <div
                          key={definition.id}
                          className={cn(
                            fullWidth && group.fieldLayout === 'TWO_COLUMNS' && 'sm:col-span-2',
                          )}
                        >
                          <CustomFieldControl
                            definition={definition}
                            unresolvedSourceValue={unresolved ? unresolvedSourceValue : null}
                            value={values[definition.key]}
                            onChange={(value) => onChange?.(definition.key, value)}
                            error={errors[definition.key]}
                            disabled={disabled}
                            computed={computedValues[definition.key]}
                            inputId={`${idPrefix}-${definition.key}`}
                          />
                        </div>
                      );
                    }

                    const formatted = unresolved
                      ? unresolvedSourceValue
                      : formatCustomFieldValue(definition, values[definition.key], i18n.language);
                    return (
                      <div
                        key={definition.id}
                        className={cn(
                          'min-w-0',
                          fullWidth && group.fieldLayout === 'TWO_COLUMNS' && 'sm:col-span-2',
                        )}
                      >
                        <p className="text-xs text-muted-foreground">
                          {customFieldLabel(definition, i18n.language)}
                        </p>
                        <p className="whitespace-pre-wrap break-words text-sm">
                          {formatted === '' ? '—' : formatted}
                        </p>
                        {unresolved ? (
                          <p className="text-xs text-muted-foreground">
                            {t('migration.nativeFieldUnresolved')}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                {hasUnpublishedFields ? (
                  <details className="mt-3 text-xs text-muted-foreground">
                    <summary className="min-h-11 cursor-pointer py-3">
                      {t('migration.fieldAvailability')}
                    </summary>
                    <p>{t('migration.nativeFieldUnpublished')}</p>
                  </details>
                ) : null}
              </CardContent>
            ) : null}
          </Card>
        );
      })}
    </section>
  );
}
